import { prettyDateTime } from "@app/date";
import {
  fetchBackupRp,
  fetchDatabase,
  fetchDatabasesByEnvId,
  pitrRestoreDatabase,
  selectDatabaseById,
  selectDatabasesByEnvId,
  selectLatestBackupRpByEnvId,
} from "@app/deploy";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import { databaseDetailUrl, environmentActivityUrl } from "@app/routes";
import { handleValidator } from "@app/validator";
import type { CalendarDateTime } from "@internationalized/date";
import {
  now,
  parseAbsolute,
  toCalendarDateTime,
} from "@internationalized/date";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useLoaderSuccess } from "starfx/react";
import { useValidator } from "../hooks";
import {
  BannerMessages,
  Box,
  BoxGroup,
  ButtonCreate,
  FormGroup,
  Group,
  Input,
  InputDate,
  PitrMisconfigurationWarning,
} from "../shared";

interface RecoveryFormData {
  dbId: string;
  handle: string;
  recoveryTargetType: string;
  recoveryTarget: CalendarDateTime | undefined;
}

export const DatabaseRecoveryPage = () => {
  const { id = "" } = useParams();
  useQuery(fetchDatabase({ id }));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const database = useSelector((s) => selectDatabaseById(s, { id }));

  useQuery(fetchDatabasesByEnvId({ envId: database.environmentId }));
  const existingDatabases = useSelector((s) =>
    selectDatabasesByEnvId(s, { envId: database.environmentId }),
  );

  useQuery(fetchBackupRp({ envId: database.environmentId }));
  const backupRp = useSelector((s) =>
    selectLatestBackupRpByEnvId(s, { envId: database.environmentId }),
  );

  if (database.id && !database?.enablePitr) {
    navigate(databaseDetailUrl(database.id));
  }

  const validators = {
    handle: (data: RecoveryFormData) => {
      const validationError = handleValidator(data.handle);
      if (validationError) return validationError;

      const isDuplicate = existingDatabases.some(
        (db) => db.handle === data.handle,
      );
      if (isDuplicate) {
        return "A database with this handle already exists in this environment";
      }
    },
    recoveryTarget: ({ recoveryTarget }: RecoveryFormData) => {
      if (!recoveryTarget) return "Target time is required";

      if (recoveryTarget < minRecoveryTarget) {
        return `Target time must be after ${prettyDateTime(minRecoveryTarget.toString())}`;
      }

      if (recoveryTarget > maxRecoveryTarget) {
        return `Target time must be before ${maxRecoveryTarget
          .toString()
          .replace("T", " ")
          .replace(/:\d{2}(\.\d+)?$/, "")} UTC`;
      }
    },
  };
  const [handle, setHandle] = useState("");
  const [recoveryTargetType] = useState("time");
  // NOTE: We aren't using luxon for dates because <DatePicker /> expects a DateValue and conversion between the two libraries is tricky.
  const [currentTime] = useState(toCalendarDateTime(now("UTC")));
  const [recoveryTarget, setRecoveryTarget] = useState<
    CalendarDateTime | undefined
  >();
  const [minRecoveryTarget, setMinRecoveryTarget] = useState(currentTime);
  const maxRecoveryTarget = toCalendarDateTime(now("UTC"));
  const [errors, validate] = useValidator<RecoveryFormData, typeof validators>(
    validators,
  );

  const data: RecoveryFormData = {
    dbId: id,
    handle,
    recoveryTargetType,
    recoveryTarget,
  };

  const action = pitrRestoreDatabase({
    ...data,
    recoveryTarget: recoveryTarget?.toString(),
  });
  const loader = useLoader(action);

  useEffect(() => {
    setHandle(`${database.handle}-recovered`);
  }, [database.handle]);

  useEffect(() => {
    const fromPolicy = currentTime.subtract({ days: backupRp.pitrDays });
    const fromDatabase = database.oldestPitrTime
      ? toCalendarDateTime(parseAbsolute(database.oldestPitrTime, "UTC"))
      : null;
    setMinRecoveryTarget(
      fromDatabase && fromDatabase > fromPolicy ? fromDatabase : fromPolicy,
    );
  }, [backupRp.pitrDays, database.oldestPitrTime]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate(data)) return;
    dispatch(action);
  };

  useLoaderSuccess(loader, () => {
    navigate(environmentActivityUrl(database.environmentId));
  });

  return (
    <BoxGroup>
      <Box>
        <div className="flex justify-between items-start">
          <h1 className="text-lg text-gray-500 mb-4">Point-in-time Recovery</h1>
        </div>
        <div className="mb-4">
          <BannerMessages {...loader} />

          {backupRp.daily <= backupRp.pitrDays ? (
            <PitrMisconfigurationWarning
              addLinkToEnvironmentPolicy
              environmentId={database.environmentId}
            />
          ) : null}
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Group>
            <FormGroup
              label="New Database Name"
              htmlFor="dbname"
              feedbackMessage={errors.handle}
              feedbackVariant={errors.handle ? "danger" : "info"}
              description={
                <p>
                  You can recover this database using a point-in-time recovery
                  at target time. A new database will be created from the latest
                  backup relevant to the target time. Once created, all archived
                  write-ahead logs (WAL) up to the specified time will be
                  applied during provisioning.
                </p>
              }
            >
              <Input
                className="max-w-xs"
                name="dbname"
                value={handle}
                onChange={(e) => setHandle(e.currentTarget.value)}
              />
            </FormGroup>
            <FormGroup
              label="Target Time (UTC)"
              htmlFor="target-time"
              feedbackMessage={errors.recoveryTarget}
              feedbackVariant={errors.recoveryTarget ? "danger" : "info"}
              description={
                <p>
                  Recovery is available for any time after{" "}
                  <b>{prettyDateTime(minRecoveryTarget.toString())}</b>
                </p>
              }
            >
              <InputDate
                aria-label="target-time"
                className="max-w-xs"
                name="target-time"
                granularity="second"
                hourCycle={24}
                minValue={minRecoveryTarget}
                maxValue={maxRecoveryTarget}
                value={recoveryTarget}
                onChange={(d) => d && setRecoveryTarget(d)}
              />
            </FormGroup>
            <Group variant="horizontal" size="sm">
              <ButtonCreate
                envId={database.environmentId}
                isLoading={loader.isLoading}
                onClick={onSubmit}
                disabled={handle === "" || !recoveryTarget}
              >
                Start Recovery
              </ButtonCreate>
            </Group>
          </Group>
        </form>
      </Box>
    </BoxGroup>
  );
};
